import { asReferencedResourceId, asTaskId, type TaskId } from "@repo/domain";
import { z } from "zod";
import { fhirRefPolySchema, fhirRefSchema } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { patientRefSchema } from "./patient.schemas";
import { practitionerRefSchema } from "./practitioner.schemas";

// === Value schemas ===

export const taskPrioritySchema = z.enum(["routine", "urgent", "asap", "stat"]);

// === Intent schemas (one per handler — shared between entry + handler) ===

// Allowed target resource types for Task references. Narrow to what our
// workflows actually produce — extend as new targets become real.
const taskOwnerRefSchema = z.union([
  practitionerRefSchema,
  fhirRefSchema("Organization").transform((ref) => ({ ...ref, id: asReferencedResourceId(ref.id) })),
  fhirRefSchema("CareTeam").transform((ref) => ({ ...ref, id: asReferencedResourceId(ref.id) })),
]);
// Requester/author come from ctx.profile — must accept every profile type.
const taskRequesterRefSchema = z.union([
  practitionerRefSchema,
  fhirRefPolySchema("Organization", "Patient", "RelatedPerson"),
]);

// draftTask — external input (client-sent fields)
export const createTaskInputSchema = z.object({
  id: newEntityIdSchema,
  description: z.string().min(1),
  status: z.enum(["draft", "ready"]).optional(),
  priority: taskPrioritySchema.optional(),
  patient: patientRefSchema.optional(),
  owner: taskOwnerRefSchema.optional(),
  dueDate: z.string().optional(),
});
export type CreateTaskInput = z.infer<typeof createTaskInputSchema>;

// draftTask — full intent (external + context enrichment)
export const draftTaskIntentSchema = createTaskInputSchema.extend({
  requester: taskRequesterRefSchema.optional(),
});
export type DraftTaskInput = z.infer<typeof draftTaskIntentSchema>;

// Every intent that names an existing request.
export const taskIdSchema: z.ZodType<TaskId, z.ZodTypeDef, string> = z.string().min(1).transform(asTaskId);
export const taskIdInputSchema = z.object({ taskId: taskIdSchema });
export type TaskIdInput = z.infer<typeof taskIdInputSchema>;

// retractTask — one domain state, two FHIR spellings. `cancelled` is somebody
// calling it off; `failed` is it having been attempted and not worked.
export const retractTaskInputSchema = taskIdInputSchema.extend({
  outcome: z.enum(["cancelled", "failed"]).optional(),
});
export type RetractTaskInput = z.infer<typeof retractTaskInputSchema>;

// assignTask
export const assignTaskInputSchema = z.object({
  taskId: taskIdSchema,
  owner: taskOwnerRefSchema,
});
export type AssignTaskInput = z.infer<typeof assignTaskInputSchema>;

// setTaskStatus — single intent for status transitions. Domain validates the
// transition map and applies per-target side effects (executionPeriod start/end,
// cancel reason). `draft`/`requested` are creation-only states, not targets.
export const settableTaskStatusSchema = z.enum(["ready", "in-progress", "on-hold", "completed", "cancelled", "failed"]);
export type SettableTaskStatus = z.infer<typeof settableTaskStatusSchema>;

export const setTaskStatusInputSchema = z.object({
  taskId: taskIdSchema,
  status: settableTaskStatusSchema,
});
export type SetTaskStatusInput = z.infer<typeof setTaskStatusInputSchema>;

// advanceTask — the roster half of the above. Terminal statuses are absent by
// design: completion and retraction are the entity's, and reach FHIR through
// their own handlers so who ended it and why gets recorded.
export const advanceTaskInputSchema = z.object({
  taskId: taskIdSchema,
  status: z.enum(["ready", "in-progress", "on-hold"]),
});
export type AdvanceTaskInput = z.infer<typeof advanceTaskInputSchema>;

// setTaskField
export const setTaskFieldInputSchema = z.object({
  taskId: taskIdSchema,
  priority: taskPrioritySchema.optional(),
  dueDate: z.string().nullable().optional(),
  description: z.string().min(1).optional(),
});
export type SetTaskFieldInput = z.infer<typeof setTaskFieldInputSchema>;

// addComment — external input (client-sent fields)
export const addCommentExternalSchema = z.object({
  taskId: taskIdSchema,
  text: z.string().min(1),
  recipients: z.array(z.object({ reference: z.string(), display: z.string().optional() })).optional(),
});

// addComment — full intent (external + context enrichment). Author comes from
// ctx.profile — accept any profile type.
export const addCommentIntentSchema = addCommentExternalSchema.extend({
  author: z.union([practitionerRefSchema, fhirRefPolySchema("Organization", "Patient", "RelatedPerson")]).optional(),
});
export type AddCommentInput = z.infer<typeof addCommentIntentSchema>;

// Legacy type exports (kept for backwards compatibility with data-store)
export const updateTaskFieldSchema = setTaskFieldInputSchema;
export type UpdateTaskFieldInput = z.infer<typeof updateTaskFieldSchema>;
